import { fmtCompetencia } from './formatters';
import { fmtSaldo, faixaDoEmpenho } from './empenho';

/**
 * O EMPENHO QUE ESTÁ ACABANDO NO DASHBOARD FISCAL (E4, 04/11/2026; backend #603, `GET /fiscal/dashboard` →
 * `empenhosAcabando[]`). A regra de QUEM entra mora no backend (`empenhosAcabando`, função pura); aqui só a frase e a
 * ação de cada código. ⛔ A tela decide pelo CÓDIGO e nunca refaz o limiar (`<= 1`, `saldo < média`): o backend separou
 * os motivos justamente para o limiar existir num lugar só.
 *
 * As ações são diferentes, e por isso o cartão as conta separadas: acabar o TEMPO ou o ORÇAMENTO pede o PRÓXIMO
 * empenho ao órgão; o saldo que não cobre a próxima pede REFORÇO do mesmo. Código desconhecido aparece como veio, sem
 * ação: adivinhar a ação de um motivo novo seria dizer ao operador o que pedir sem saber.
 */
export type MotivoDoEmpenhoAcabando = 'ULTIMA_COMPETENCIA_CALENDARIO' | 'ULTIMA_COMPETENCIA_ORCAMENTO' | 'SALDO_NAO_COBRE_A_PROXIMA';

export interface EmpenhoAcabando {
  empenhoId: number;
  number: string;
  contractId?: number | null;
  /** O nome de exibição do cliente, pronto do servidor (backend #604). Null quando a linha não tem cliente resolvível: a tela omite, sem inventar "assinatura #N". */
  client?: string | null;
  competenciaInicio: string;
  competenciaFim: string;
  competenciasRestantes: number;
  mesesAteOFim: number;
  saldo: number;
  mediaPorCompetencia: number | null;
  motivos: Array<MotivoDoEmpenhoAcabando | string>;
}

export type AcaoDoEmpenho = 'proximo' | 'reforco';
export const ROTULO_DA_ACAO: Record<AcaoDoEmpenho, string> = {
  proximo: 'pedir o próximo empenho',
  reforco: 'pedir reforço deste empenho',
};

export interface LinhaDoMotivo {
  codigo: string;
  frase: string;
  acao: AcaoDoEmpenho | null;
}

const competencias = (n: number) => (n === 1 ? '1 competência' : `${n} competências`);

export function linhaDoMotivo(codigo: string, e: EmpenhoAcabando): LinhaDoMotivo {
  switch (codigo) {
    case 'ULTIMA_COMPETENCIA_CALENDARIO':
      return { codigo, frase: `Último mês da faixa (${fmtCompetencia(e.competenciaFim)}).`, acao: 'proximo' };
    case 'ULTIMA_COMPETENCIA_ORCAMENTO':
      return {
        codigo,
        frase: e.competenciasRestantes > 0 ? `Resta ${competencias(e.competenciasRestantes)} de orçamento.` : 'Nenhuma competência de orçamento restante.',
        acao: 'proximo',
      };
    case 'SALDO_NAO_COBRE_A_PROXIMA':
      return {
        codigo,
        frase: `O saldo de ${fmtSaldo(e.saldo)} não cobre a próxima${e.mediaPorCompetencia != null ? ` (média ${fmtSaldo(e.mediaPorCompetencia)} por competência)` : ''}.`,
        acao: 'reforco',
      };
    default:
      return { codigo, frase: codigo, acao: null };
  }
}

/** As ações do empenho, sem repetir: tempo e orçamento acabando pedem a mesma coisa. */
export function acoesDoEmpenho(e: EmpenhoAcabando): AcaoDoEmpenho[] {
  const acoes = (e.motivos || []).map((m) => linhaDoMotivo(String(m), e).acao).filter((a): a is AcaoDoEmpenho => !!a);
  return Array.from(new Set(acoes));
}

/** O sub do cartão: "1 pede o próximo · 1 pede reforço". Um empenho com as duas ações conta nas duas. */
export function resumoDoCartao(lista: EmpenhoAcabando[]): string {
  const conta = (a: AcaoDoEmpenho) => lista.filter((e) => acoesDoEmpenho(e).includes(a)).length;
  const proximo = conta('proximo');
  const reforco = conta('reforco');
  const partes = [
    proximo ? `${proximo} ${proximo === 1 ? 'pede' : 'pedem'} o próximo` : '',
    reforco ? `${reforco} ${reforco === 1 ? 'pede' : 'pedem'} reforço` : '',
  ].filter(Boolean);
  return partes.length ? partes.join(' · ') : 'ver os motivos';
}

/** A linha de números do empenho: saldo, orçamento restante e média (quando há consumo). */
export function numerosDoEmpenho(e: EmpenhoAcabando): string {
  return [
    `saldo ${fmtSaldo(e.saldo)}`,
    `${competencias(e.competenciasRestantes)} de orçamento ${e.competenciasRestantes === 1 ? 'restante' : 'restantes'}`,
    e.mediaPorCompetencia != null ? `média ${fmtSaldo(e.mediaPorCompetencia)} por competência` : 'sem consumo ainda',
  ].join(' · ');
}

export { faixaDoEmpenho };
